import {Box, Checkbox, CircularProgress, FormControlLabel, Typography} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import useQuery from "../../../../../../hooks/use-query";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useSearch from "../../../../../../hooks/use-search";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import Api from "../../../../../../../core/services/api/api";
import {createUUId} from "../../../../../../../core/services/utils/utils";
import useDynamicColumns from "../../../../../../hooks/use-dynamic-columns";
import QueryBuilder, {QueryBuilderUtils} from "../../../../../../../packages/query-builder";
import useReportingSearchUtils from "../../../../../../hooks/use-reporting-search-utils";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import {Col} from "reactstrap";


const queryBuilderSaveName = 'LostSalesAnalyticsLogsViewQueryBuilder';
const dynamicColumnsSectionName = 'LostSales';
const dataGridSaveConfig = {
    name: 'LostSalesAnalyticsLogsViewDataGrid',
    version: 1,
}


const staticColumnsNames = {
    isChecked: 'isChecked',
}


const AnalyticsLostSearchLogsView = () => {
    const [data, setData] = useState([]);
    const [query, setQuery] = useQuery();
    const [toggling, setToggling] = useState([])
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportLostSalesLogs = operatorAccessLevels.packages.dashboard.lostSalesLogs
        && operatorAccessLevels.packages.dashboard.exportLostSalesLogs;

    const dataGridId = useMemo(() => createUUId(true), []);
    const staticDataGridColumns = useMemo(() => [], []);

    const dataGridColumnsConfig = useMemo(() => ({}), [])

    const [dynamicColumnsLoading, queryBuilderColumns, dataGridColumns] = useDynamicColumns({
        sectionName: dynamicColumnsSectionName,
        staticDataGridColumns: staticDataGridColumns,
        dataGridColumnsConfig: dataGridColumnsConfig,
    });

    const [savedDataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: dataGridColumns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])


    /**
     * Fetches the entries from the server based on the user's filters.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const siftedFilters = QueryBuilderUtils.getApiQueryFromStateQuery(args.filters, queryBuilderColumns);
        const forApi = {
            reportingBaseModel: {
                keyword: siftedFilters.keyword,
                filters: siftedFilters.filters ?? [],
            },
            pagination: args.paginationInfo,
            sorts: args.orderBy ? [{isDesc: args.orderBy.descending, property: args.orderBy.field,}] : [],
        }
        const response = await Api.listReportedLostSales(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, queryBuilderColumns])

    /**
     * Searches among the entries based on the filters from the user.
     */
    const search = useCallback(async (args) => {
        await fetchEntries(args, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {...(query.filters ? JSON.parse(query.filters) : {})},
    }), [initialPaginationInfo, dataGridSortBy, query.filters]);

    const searchData = useSearch(search, initialSearchDataSettings, false)
    const {setFilters} = searchData;

    /**
     * When the filters change, update the search data
     */
    useEffect(() => {
        setFilters({...(query.filters ? JSON.parse(query.filters) : {})})
    }, [setFilters, query]);

    /**
     * Toggles the checked status of the entry.
     * @type {(function(*, *): Promise<void>)}
     */
    const toggleChecked = useCallback(async (id, value) => {
        setToggling(prevState => [...prevState, id]);
        const response = await Api.toggleReportLostSaleCheck(id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === id ? {...e, isChecked: value} : e));
        }
        setToggling(prevState => prevState.filter(e => e !== id));
    }, [isMounted])

    /**
     * Formats the exported cell based on the column type.
     */
    const getDataGridCell = useCallback((data, column, continueMarker) => {
        switch (column.name) {
            case staticColumnsNames.isChecked:
                return (
                    toggling.includes(data.id)
                        ? <div className={'p-2'}>
                            <CircularProgress size={15}/>
                        </div>
                        : <FormControlLabel
                            sx={{margin: 0}}
                            control={
                                <Checkbox
                                    disabled={toggling.includes(data.id)}
                                    sx={{paddingBlock: 0.75}}
                                    disableRipple
                                    checked={data[column.name] ?? false}
                                    onChange={(e, checked) => toggleChecked(data.id, checked)}
                                />
                            }
                        />
                )
            default:
                return continueMarker
        }
    }, [toggleChecked, toggling])


    const {
        getExportInfo,
        dataGridRows,
    } = useReportingSearchUtils({
        savedDataGridColumns,
        fetchEntries,
        searchData,
        getDataGridCell,
        data,
        exportingTitle: 'Lost Sales Analytics Logs',
    });

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    Lost-sales Logs
                </Typography>
                {
                    dynamicColumnsLoading &&
                    <>
                        <LoadingLottieAnimation
                            width={'80%'}
                            className={'my-5 py-5'}
                        />
                    </>
                }
                {
                    !dynamicColumnsLoading &&
                    <>
                        <QueryBuilder
                            columns={queryBuilderColumns}
                            name={queryBuilderSaveName}
                            search={(query) => searchData.setFilters(query) || setQuery(prevState => ({...prevState, filters: JSON.stringify(query)}))}
                            query={searchData.filters}
                            loading={searchData.loading}
                            dataGridId={dataGridId}
                        />
                        <div className='w-100 my-2'/>
                        <PartnerDataGrid
                            id={dataGridId}
                            {...dataGridSaveConfig}
                            initialLayoutState={{columns: dataGridColumns}}
                            columns={savedDataGridColumns}
                            rows={dataGridRows}
                            hideFooterActions
                            state={{
                                sortBy: searchData.orderBy,
                                pagination: searchData.paginationInfo,
                                loading: {state: searchData.loading},
                                density: dataGridDensity,
                            }}
                            getExportInfo={canExportLostSalesLogs ? getExportInfo : undefined}
                            onSortByChanged={(_, current) =>
                                searchData.setOrderBy?.(current)}
                            onCurrentPageChanged={(_, current) =>
                                searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                            onPageSizeChanged={(_, current) =>
                                searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        />
                        <Col className={'mt-4'}/>
                    </>
                }
            </Box>
        </>
    );
}

export default AnalyticsLostSearchLogsView;